import React, { useEffect, useState } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Box, Card, CardContent, Chip, CircularProgress,
  TextField, Button, Select, MenuItem, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Grid,
} from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import InfoIcon from "@mui/icons-material/Info";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:4000";

const decodeToken = (token) => {
  try {
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1]));
    return { email: payload.email || 'Unknown User' };
  } catch { return null; }
};

const getStatusStyle = (status) => {
  const map = {
    answered: { bg: "rgba(16,185,129,0.08)", color: "#059669", label: "Completed" },
    completed: { bg: "rgba(16,185,129,0.08)", color: "#059669", label: "Completed" },
    busy: { bg: "rgba(239,68,68,0.08)", color: "#dc2626", label: "Busy" },
    "no-answer": { bg: "rgba(245,158,11,0.08)", color: "#d97706", label: "No Answer" },
    failed: { bg: "rgba(239,68,68,0.08)", color: "#dc2626", label: "Failed" },
    initiated: { bg: "rgba(99,102,241,0.08)", color: "#6366f1", label: "Initiated" },
    declined: { bg: "rgba(239,68,68,0.08)", color: "#dc2626", label: "Declined" },
  };
  return map[status] || { bg: "rgba(100,116,139,0.08)", color: "#64748b", label: status || "Unknown" };
};

const getChoiceStyle = (choice) => {
  if (choice === "1" || choice === "confirm") return { bg: "rgba(16,185,129,0.08)", color: "#059669", label: "Confirmed" };
  if (choice === "2" || choice === "decline") return { bg: "rgba(239,68,68,0.08)", color: "#dc2626", label: "Declined" };
  return { bg: "rgba(100,116,139,0.08)", color: "#64748b", label: choice || "—" };
};

const calculateDuration = (start, end) => {
  if (!start || !end) return "—";
  try {
    const s = new Date(start), e = new Date(end);
    if (isNaN(s) || isNaN(e)) return "—";
    return `${Math.max(0, Math.round((e - s) / 1000))}s`;
  } catch { return "—"; }
};

export default function IVRDashboard({ token }) {
  const authToken = token || localStorage.getItem("token");
  const userEmail = decodeToken(authToken)?.email || "Guest";
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchPhone, setSearchPhone] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [stats, setStats] = useState({});
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  const getAuthHeaders = () => ({ Authorization: `Bearer ${authToken}` });

  const fetchData = async () => {
    try {
      const statusParam = filterStatus !== "all" ? `&status=${filterStatus}` : "";
      const phoneParam = searchPhone ? `&phoneNumber=${searchPhone}` : "";
      const res = await fetch(`${BACKEND_URL}/api/ivr/captures?limit=100${statusParam}${phoneParam}`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error();
      const result = await res.json();
      setRecords(result.data || []);
      setLastUpdate(new Date());
      try {
        const sr = await fetch(`${BACKEND_URL}/api/ivr/stats`, { headers: getAuthHeaders() });
        if (sr.ok) setStats(await sr.json());
      } catch {}
    } catch (err) {
      console.error("Error fetching IVR data:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, 30000);
    return () => clearInterval(id);
  }, [filterStatus, searchPhone]);

  const downloadCSV = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/ivr/export`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error();
      const csv = await res.text();
      const blob = new Blob([csv], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ivr_calls_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { alert("Failed to download CSV"); }
  };

  const statCards = [
    { label: "Total Calls", value: stats.totalCalls || 0, color: "#6366f1", bg: "rgba(99,102,241,0.06)", border: "rgba(99,102,241,0.15)" },
    { label: "Answered", value: stats.answeredCalls || 0, color: "#2563eb", bg: "rgba(59,130,246,0.06)", border: "rgba(59,130,246,0.15)" },
    { label: "Confirmed", value: stats.confirmedCalls || 0, color: "#d97706", bg: "rgba(245,158,11,0.06)", border: "rgba(245,158,11,0.15)" },
    { label: "Completed", value: stats.completedFlows || 0, color: "#059669", bg: "rgba(16,185,129,0.06)", border: "rgba(16,185,129,0.15)" },
    { label: "Avg Duration", value: `${Math.round(stats.avgDuration || 0)}s`, color: "#6366f1", bg: "rgba(99,102,241,0.06)", border: "rgba(99,102,241,0.15)" },
  ];

  return (
    <div className="page-wrapper animate-in">
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 4, flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ color: "#0f172a", mb: 0.5 }}>IVR Capture Dashboard</Typography>
          <Typography sx={{ color: "#64748b", fontSize: "0.85rem" }}>
            Last updated: {lastUpdate.toLocaleTimeString()} · Auto-refresh every 30s
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={() => { setRefreshing(true); fetchData(); }} disabled={refreshing} sx={{ background: "#f1f5f9", "&:hover": { background: "#e2e8f0" } }}>
              <RefreshIcon sx={{ fontSize: 20, animation: refreshing ? "spin 1s linear infinite" : "none", "@keyframes spin": { "100%": { transform: "rotate(360deg)" } } }} />
            </IconButton>
          </Tooltip>
          <Button variant="contained" size="small" startIcon={<DownloadIcon />} onClick={downloadCSV} sx={{ fontSize: "0.8rem" }}>
            Export CSV
          </Button>
        </Box>
      </Box>

      {/* Stats */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 2, mb: 3 }}>
        {statCards.map((s) => (
          <Card key={s.label} sx={{ background: s.bg, border: `1px solid ${s.border}`, boxShadow: "none" }}>
            <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
              <Typography sx={{ color: "#64748b", fontSize: "0.8rem", fontWeight: 500, mb: 0.5 }}>{s.label}</Typography>
              <Typography sx={{ color: s.color, fontWeight: 700, fontSize: "1.5rem" }}>{s.value}</Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Search & Filters */}
      <Box className="enterprise-card" sx={{ p: 3 }}>
        <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap", alignItems: "center" }}>
          <form onSubmit={(e) => { e.preventDefault(); setLoading(true); fetchData(); }} style={{ display: "flex", gap: 8, flex: 1, minWidth: 200 }}>
            <TextField fullWidth size="small" placeholder="Search by phone number..." value={searchPhone} onChange={(e) => setSearchPhone(e.target.value)}
              InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: "#94a3b8", fontSize: 20 }} /> }}
            />
            <Button type="submit" variant="contained" size="small" sx={{ minWidth: 80 }}>Search</Button>
          </form>
          <Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} size="small" sx={{ minWidth: 140 }}>
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="answered">Answered</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="initiated">Initiated</MenuItem>
            <MenuItem value="busy">Busy</MenuItem>
            <MenuItem value="no-answer">No Answer</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
            <MenuItem value="declined">Declined</MenuItem>
          </Select>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={32} sx={{ color: "#6366f1" }} />
          </Box>
        ) : records.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <PhoneIcon sx={{ fontSize: 48, color: "#cbd5e1", mb: 1 }} />
            <Typography sx={{ fontWeight: 600, color: "#64748b" }}>No records found</Typography>
          </Box>
        ) : (
          <TableContainer sx={{ borderRadius: "10px", border: "1px solid #f1f5f9" }}>
            <Table>
              <TableHead sx={{ background: "#f8fafc" }}>
                <TableRow>
                  {["Phone", "Call Status", "First Input", "Captured Data", "Flow Status", "Duration", "Date", ""].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 600, color: "#64748b" }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {records.map((r, i) => {
                  const s = getStatusStyle(r.status);
                  const c = getChoiceStyle(r.firstInputDigit);
                  return (
                    <TableRow key={r.id || i} sx={{ "&:hover": { background: "#fafafa" } }}>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <PhoneIcon sx={{ color: "#94a3b8", fontSize: 16 }} />
                          {r.phoneNumber}
                        </Box>
                      </TableCell>
                      <TableCell><Chip label={s.label} size="small" sx={{ background: s.bg, color: s.color, border: `1px solid ${s.color}20` }} /></TableCell>
                      <TableCell>
                        {r.firstInputDigit ? <Chip label={c.label} size="small" sx={{ background: c.bg, color: c.color, border: `1px solid ${c.color}20` }} /> : "—"}
                      </TableCell>
                      <TableCell>
                        {r.employeeId ? (
                          <Chip label={r.employeeId.length > 4 ? `****${r.employeeId.slice(-4)}` : r.employeeId} size="small"
                            sx={{ background: "rgba(99,102,241,0.06)", color: "#6366f1", fontFamily: "monospace" }}
                          />
                        ) : "—"}
                      </TableCell>
                      <TableCell>
                        <Chip label={r.flowStatus || "initiated"} size="small" variant="outlined"
                          sx={{ borderColor: r.flowStatus === "completed" ? "#059669" : r.flowStatus === "declined" ? "#dc2626" : "#6366f1",
                                color: r.flowStatus === "completed" ? "#059669" : r.flowStatus === "declined" ? "#dc2626" : "#6366f1" }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontVariantNumeric: "tabular-nums" }}>{calculateDuration(r.callStartTime, r.callEndTime)}</TableCell>
                      <TableCell sx={{ color: "#64748b", fontSize: "0.82rem" }}>{new Date(r.createdAt).toLocaleString()}</TableCell>
                      <TableCell>
                        <Tooltip title="View details">
                          <IconButton size="small" onClick={() => setSelectedRecord(r)} sx={{ color: "#6366f1" }}>
                            <InfoIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Detail dialog */}
      <Dialog open={Boolean(selectedRecord)} onClose={() => setSelectedRecord(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "16px" } }}>
        <DialogTitle sx={{ fontWeight: 700, color: "#0f172a", pb: 1 }}>Call Details</DialogTitle>
        <DialogContent>
          {selectedRecord && (
            <Box sx={{ display: "grid", gap: 2, mt: 1 }}>
              {[
                ["Phone Number", selectedRecord.phoneNumber],
                ["Call Status", selectedRecord.status || "initiated"],
                ["First Input", selectedRecord.firstInputDigit || "—"],
                ["Captured Data", selectedRecord.employeeId || "—"],
                ["Flow Status", selectedRecord.flowStatus || "initiated"],
                ["Duration", calculateDuration(selectedRecord.callStartTime, selectedRecord.callEndTime)],
                ["Created", new Date(selectedRecord.createdAt).toLocaleString()],
                ...(selectedRecord.callStartTime ? [["Call Start", new Date(selectedRecord.callStartTime).toLocaleString()]] : []),
                ...(selectedRecord.callEndTime ? [["Call End", new Date(selectedRecord.callEndTime).toLocaleString()]] : []),
              ].map(([label, value]) => (
                <Box key={label}>
                  <Typography sx={{ color: "#94a3b8", fontSize: "0.75rem", fontWeight: 500, mb: 0.3 }}>{label}</Typography>
                  <Typography sx={{ fontWeight: 600, fontSize: "0.9rem", color: "#0f172a" }}>{value}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setSelectedRecord(null)} sx={{ fontWeight: 600 }}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
